/**
 * API utility functions for making requests
 */

// Get API base URL from environment variable or use default
// In production, this should be set via REACT_APP_API_URL
// If not set, defaults to relative path (works with nginx proxy)
// Normalize the URL to always include /api/v1
let API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// If REACT_APP_API_URL is set (absolute URL), ensure it includes /api/v1
if (API_BASE_URL.startsWith('http')) {
  // Remove trailing slash if present
  API_BASE_URL = API_BASE_URL.replace(/\/$/, '');
  // Append /api/v1 if not already present
  if (!API_BASE_URL.endsWith('/api/v1')) {
    API_BASE_URL = `${API_BASE_URL}/api/v1`;
  }
}

/**
 * Fetch data from API endpoint
 * @param {string} endpoint - API endpoint (relative to /api/v1)
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} JSON response data
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('/') 
    ? `${API_BASE_URL}${endpoint}` 
    : `${API_BASE_URL}/${endpoint}`;
  
  // Debug logging
  console.log('[apiFetch] Request URL:', url);
  console.log('[apiFetch] API_BASE_URL:', API_BASE_URL);
  console.log('[apiFetch] REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  console.log('[apiFetch] Response status:', response.status, response.statusText);
  console.log('[apiFetch] Response headers:', Object.fromEntries(response.headers.entries()));
  
  // Check content-type before reading body
  const contentType = response.headers.get('content-type');
  console.log('[apiFetch] Content-Type:', contentType);
  
  // Clone response to read body for logging without consuming it
  const responseClone = response.clone();
  const responseText = await responseClone.text();
  console.log('[apiFetch] Response text preview (first 200 chars):', responseText.substring(0, 200));
  
  if (!response.ok) {
    console.error('[apiFetch] Error response body:', responseText.substring(0, 500));
    throw new Error(`API request failed: ${response.status} ${response.statusText}. Response: ${responseText.substring(0, 200)}`);
  }
  
  // Check content-type before parsing as JSON
  if (!contentType || !contentType.includes('application/json')) {
    console.error('[apiFetch] Expected JSON but got:', contentType);
    console.error('[apiFetch] Full response text:', responseText);
    throw new Error(`Expected JSON response but got ${contentType}. Response: ${responseText.substring(0, 200)}`);
  }
  
  // Parse original response as JSON
  try {
    const data = await response.json();
    console.log('[apiFetch] Successfully parsed JSON response');
    return data;
  } catch (parseError) {
    console.error('[apiFetch] JSON parse error:', parseError);
    console.error('[apiFetch] Response text:', responseText);
    throw new Error(`Failed to parse JSON response: ${parseError.message}. Response: ${responseText.substring(0, 200)}`);
  }
};

/**
 * Fetch company data
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<any>} Company data
 */
export const fetchCompany = async (ticker) => {
  return apiFetch(`/companies/${ticker}`);
};

/**
 * Fetch price history
 * @param {string} ticker - Stock ticker symbol
 * @param {number} days - Number of days of history
 * @returns {Promise<any>} Price history data
 */
export const fetchPriceHistory = async (ticker, days = 365) => {
  return apiFetch(`/companies/${ticker}/price-history?days=${days}`);
};

/**
 * Fetch trading signals
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<any>} Signal data
 */
export const fetchSignals = async (ticker) => {
  return apiFetch(`/companies/${ticker}/signals`);
};

/**
 * Fetch all companies
 * @param {Object} params - Query parameters (limit, offset)
 * @returns {Promise<any[]>} Array of companies
 */
export const fetchCompanies = async (params = {}) => {
  const { limit = 50, offset = 0 } = params;
  return apiFetch(`/companies?limit=${limit}&offset=${offset}`);
};

/**
 * Fetch consensus rating and price target data
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<any>} Consensus data (rating and price_target)
 */
export const fetchConsensusData = async (ticker) => {
  return apiFetch(`/companies/${ticker}/consensus`);
};

/**
 * Fetch all historical consensus rating and price target data
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<any>} Consensus history data (ratings and price_targets arrays)
 */
export const fetchConsensusHistory = async (ticker) => {
  return apiFetch(`/companies/${ticker}/consensus/history`);
};

/**
 * Fetch consensus valuation score
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<any>} Consensus valuation data (score and interpretation)
 */
export const fetchConsensusValuation = async (ticker) => {
  return apiFetch(`/companies/${ticker}/valuation`);
};

/**
 * Update company tracked status
 * @param {string} ticker - Stock ticker symbol
 * @param {boolean} tracked - Whether the stock should be tracked
 * @returns {Promise<any>} Updated company data
 */
export const updateCompanyTracked = async (ticker, tracked) => {
  return apiFetch(`/companies/${ticker}`, {
    method: 'PATCH',
    body: JSON.stringify({ tracked })
  });
};

/**
 * Fetch upgrades count (stocks upgraded from Undervalued to Very Undervalued)
 * @returns {Promise<{upgrades_count: number, timestamp: string}>}
 */
export const fetchUpgradesCount = async () => {
  return apiFetch('/stats/upgrades');
};

/**
 * Fetch leaderboard with companies and their latest consensus valuations
 * @param {Object} params - Query parameters (limit, offset, tracked)
 * @returns {Promise<Array>} Array of companies with valuation data
 */
export const fetchLeaderboard = async (params = {}) => {
  const { limit = 1000, offset = 0, tracked = null } = params;
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  });
  if (tracked !== null) {
    queryParams.append('tracked', tracked.toString());
  }
  return apiFetch(`/companies/leaderboard?${queryParams.toString()}`);
};

/**
 * Latest price ingest and consensus fetch timestamps (debug pipeline status).
 * @returns {Promise<object>} DataFreshnessResponse fields as ISO date strings or null
 */
export const fetchDataFreshness = async () => {
  return apiFetch('/debug/data-freshness');
};

/**
 * @param {{ limit?: number, tracked?: boolean|null }} params
 * @returns {Promise<{ items: { ticker: string, exchange: string }[], total: number }>}
 */
export const fetchIngestionCandidates = async (params = {}) => {
  const { limit = 1000, tracked = null, signal } = params;
  const qs = new URLSearchParams({ limit: String(limit) });
  if (tracked !== null && tracked !== undefined) {
    qs.append('tracked', String(tracked));
  }
  return apiFetch(`/debug/ingestion/candidates?${qs.toString()}`, { signal });
};

/**
 * @param {string} ticker
 * @param {{ limit?: number, signal?: AbortSignal }} options
 */
export const postIngestPriceData = async (ticker, options = {}) => {
  const { limit = 365, signal } = options;
  const qs = new URLSearchParams({ limit: String(limit) });
  return apiFetch(`/ingestion/price-data/${encodeURIComponent(ticker)}?${qs}`, {
    method: 'POST',
    signal,
  });
};

/**
 * @param {string} ticker
 * @param {{ signal?: AbortSignal }} options
 */
export const postDebugConsensus = async (ticker, options = {}) => {
  const { signal } = options;
  return apiFetch(`/debug/ingestion/consensus/${encodeURIComponent(ticker)}`, {
    method: 'POST',
    signal,
  });
};
